import DEFAULTS from "@/lib/defaultPalette.json";

export const SHADES = ["50", "100", "200", "300", "400", "500", "600", "700", "800", "900", "950"];
const STATUS = ["rose", "sky", "emerald", "pink", "violet", "orange", "stone", "red", "yellow", "purple", "lime", "fuchsia", "cyan"];
export const UI_FONTS = ["Plus Jakarta Sans", "Cormorant Garamond", "EB Garamond", "Cinzel", "IM Fell English", "Caveat", "JetBrains Mono"];
export const RADII = [["sharp", "Sharp"], ["soft", "Soft"], ["round", "Round"]];
export const COLOR_FIELDS = [["bg", "Background"], ["panel", "Panels and cards"], ["inset", "Inputs and wells"], ["line", "Borders"],
  ["text", "Text"], ["muted", "Muted text"], ["accent", "Accent"]];

const t = (bg, panel, inset, line, text, muted, accent, heading_font, body_font, radius, grain) =>
  ({ bg, panel, inset, line, text, muted, accent, heading_font, body_font, radius, grain });

export const PRESETS = [
  { id: "dark", name: "Lorely Dark", free: true, values: t("#0b0c10", "#13151c", "#0d0f14", "#282d3c", "#f1f5f9", "#94a3b8", "#f59e0b", "Cormorant Garamond", "Plus Jakarta Sans", "soft", true) },
  { id: "light", name: "Lorely Light", free: true, values: t("#f7f5f0", "#ffffff", "#efece5", "#ddd8cc", "#1c1f27", "#5c6370", "#b45309", "Cormorant Garamond", "Plus Jakarta Sans", "soft", false) },
  { id: "parchment", name: "Parchment", values: t("#efe6d2", "#f7f0df", "#e6dbc3", "#d2c3a3", "#2e2416", "#6e5d43", "#9a3412", "IM Fell English", "EB Garamond", "soft", true) },
  { id: "midnight", name: "Midnight", values: t("#0a0f1f", "#111833", "#0c1226", "#23305a", "#e6ecff", "#8a97c2", "#7dd3fc", "Cinzel", "Plus Jakarta Sans", "soft", true) },
  { id: "forest", name: "Forest", values: t("#0c130f", "#121c16", "#0e1712", "#24372b", "#e7efe6", "#8fa596", "#a3c46a", "Cormorant Garamond", "Plus Jakarta Sans", "round", true) },
  { id: "rose-noir", name: "Rose Noir", values: t("#140b0f", "#1d1117", "#170d12", "#3a2230", "#f6e9ee", "#b08b9a", "#f472b6", "EB Garamond", "Plus Jakarta Sans", "soft", true) },
  { id: "ember", name: "Ember", values: t("#120d0a", "#1b1410", "#150f0c", "#3a2a1f", "#f5ece3", "#b09a86", "#f97316", "Cinzel", "Plus Jakarta Sans", "sharp", true) },
  { id: "high-contrast", name: "High Contrast", values: t("#000000", "#0d0d0d", "#050505", "#5c5c5c", "#ffffff", "#d4d4d4", "#ffd400", "Cormorant Garamond", "Plus Jakarta Sans", "sharp", false) },
];

const rgb = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
const mix = (a, b, k) => a.map((x, i) => Math.round(x + (b[i] - x) * k));
const str = (c) => c.join(" ");
const lum = (c) => {
  const [r, g, b] = c.map((x) => { const s = x / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

// Tints lean toward the text colour and shades toward the background, so every palette reads on light and dark themes
const scale = (base, text, bg) => {
  const toText = [0.82, 0.7, 0.5, 0.3, 0.15];
  const toBg = [0.15, 0.3, 0.45, 0.6, 0.75];
  return [...toText.map((k) => mix(base, text, k)), base, ...toBg.map((k) => mix(base, bg, k))];
};
const textScale = (text, muted, bg) =>
  [text, text, mix(text, muted, 0.15), mix(text, muted, 0.35), muted, ...[0.3, 0.5, 0.65, 0.78, 0.88, 0.94].map((k) => mix(muted, bg, k))];

export const isLight = (v) => lum(rgb(v.bg)) > 0.4;

export const buildVars = (v) => {
  const [bg, panel, inset, line, text, muted, accent] = ["bg", "panel", "inset", "line", "text", "muted", "accent"].map((k) => rgb(v[k]));
  const out = {};
  textScale(text, muted, bg).forEach((c, i) => { out[`--slate-${SHADES[i]}`] = str(c); });
  scale(accent, text, bg).forEach((c, i) => { out[`--amber-${SHADES[i]}`] = str(c); });
  STATUS.forEach((p) => scale(rgb(DEFAULTS[p]["500"]), text, bg).forEach((c, i) => { out[`--${p}-${SHADES[i]}`] = str(c); }));
  const la = lum(accent);
  Object.assign(out, {
    "--c-base": str(bg), "--c-panel": str(panel), "--c-inset": str(inset), "--c-line": str(line),
    "--c-raised": str(mix(panel, line, 0.45)), "--c-line2": str(mix(line, text, 0.12)),
    "--c-veil": isLight(v) ? "0 0 0" : "255 255 255",
    "--c-onaccent": (la + 0.05) / 0.05 > 1.05 / (la + 0.05) ? "0 0 0" : "255 255 255",
    "--font-heading": `"${v.heading_font}"`, "--font-body": `"${v.body_font}"`,
    "--rs": { sharp: "0.25", soft: "1", round: "1.75" }[v.radius] || "1",
    "--r-full": v.radius === "sharp" ? "6px" : "9999px",
    "--grain-op": v.grain ? "0.05" : "0",
    "color-scheme": isLight(v) ? "light" : "dark",
  });
  return out;
};

let applied = [];
// null or the stock dark theme fall back to the stylesheet defaults
export const applyTheme = (v) => {
  const root = document.documentElement;
  applied.forEach((k) => root.style.removeProperty(k));
  applied = [];
  const stock = !v || JSON.stringify(v) === JSON.stringify(PRESETS[0].values);
  root.dataset.scheme = v && isLight(v) ? "light" : "dark";
  if (stock) return;
  Object.entries(buildVars(v)).forEach(([k, val]) => { root.style.setProperty(k, val); applied.push(k); });
};

export const CACHE_KEY = "lorely.theme";
export const cacheTheme = (v) => (v ? localStorage.setItem(CACHE_KEY, JSON.stringify(v)) : localStorage.removeItem(CACHE_KEY));
export const applyCachedTheme = () => {
  try { applyTheme(JSON.parse(localStorage.getItem(CACHE_KEY))); } catch { applyTheme(null); }
};

export const themeValues = (id, custom = []) =>
  PRESETS.find((p) => p.id === id)?.values || custom.find((c) => c.id === id)?.values || PRESETS[0].values;

// Scene break design -> props for the element wrapping the story text
export const breakProps = (d) => {
  if (!d) return {};
  if (d.kind === "image") return { "data-sb": "image", style: { "--sb-img": `url("${d.image_url}")` } };
  if (d.kind === "line") return { "data-sb": "line", style: {} };
  return { "data-sb": "text", style: { "--sb-text": JSON.stringify(d.text) } };
};
